/**
 * 机构信息表单配置文件
 *
 * 该文件定义了机构KYC流程中信息收集表单的配置结构。
 * 包含以下主要部分：
 * 1. 公司注册信息 - 公司名称、交易名称、成立日期、注册地址等
 * 2. 运营历史 - 商业活动、运营年限、员工数量、运营国家等
 * 3. 股东信息 - 股东姓名、持股比例、成立/国籍国家
 * 4. 受益人信息 - 最终受益所有人信息
 * 5. 董事和授权签字人 - 董事和授权签字人信息
 * 6. 最高级授权签字人 - 投资经验相关问题
 * 7. 资金来源 - 资金来源信息
 *
 * 注意：前三个部分（registration、operations、shareholders）使用国际化配置，
 * 后续部分（owner、directors、authorisedSignatories、mostSeniorAuthorisedSignatory、fundSources）
 */

import type { TFieldOrGroup } from "@adgm/cross-models";
import { kycCountrySelectOptionList, i18nCrossUtil, createOptions } from "@adgm/libs";
import { CUSTOM_RADIO_WITH_INPUT } from "@adgm/cross-config";

const intl = i18nCrossUtil.t();

// CategoryRadioGroup 字段选项配置
const YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS = createOptions([
  intl["kyc.v2.option.label.less.than.1.year"],
  intl["kyc.v2.option.label.1.to.2.years"],
  intl["kyc.v2.option.label.2.to.5.years"],
  intl["kyc.v2.option.label.5.to.8.years"],
  intl["kyc.v2.option.label.more.than.8.years"],
  intl["kyc.v2.option.label.no.investment.experience"],
]);

const NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS = createOptions([
  intl["kyc.v2.option.label.1.to.10"],
  intl["kyc.v2.option.label.10.to.20"],
  intl["kyc.v2.option.label.20.plus"],
  intl["kyc.v2.option.label.none"],
]);

// 机构信息表单配置
export const INSTITUTION_INFORMATION_CONFIG: TFieldOrGroup[] = [
  {
    type: "group",
    name: "registration",
    label: intl["kyc.information.registration.label"],
    horizontalLayout: true,
    fields: [
      {
        name: "companyName",
        type: "input",
        label: intl["kyc.information.registration.companyname.label"],
        validation: [{ required: true }],
      },
      {
        name: "tradingName",
        type: "input",
        label: intl["kyc.information.registration.tradingname.label"],
        validation: [{ required: false }],
      },
      {
        name: "incorporationDate",
        type: "datePicker",
        label: intl["kyc.information.registration.incorporationdate.label"],
        validation: [{ required: true }],
      },
      {
        name: "incorporationPlace",
        type: "select",
        label: intl["kyc.information.registration.incorporationplace.label"],
        options: kycCountrySelectOptionList("commonName"),
        validation: [{ required: true }],
      },
      {
        name: "registeredAddress",
        type: "input",
        label: intl["kyc.information.registration.registeredaddress.label"],
        validation: [{ required: true }],
      },
      {
        name: "mainBusinessAddress",
        type: "input",
        label: intl["kyc.information.registration.mainbusinessaddress.label"],
        validation: [{ required: true }],
      },
      {
        name: "legalForm",
        type: "input",
        label: intl["kyc.information.registration.legalform.label"],
        validation: [{ required: true }],
      },
    ],
  },
  {
    type: "group",
    name: "operations",
    label: intl["kyc.information.operations.label"],
    horizontalLayout: true,
    fields: [
      {
        name: "commercialActivities",
        type: "input",
        label: intl["kyc.information.operations.commercialactivities.label"],
        validation: [{ required: true }],
      },
      {
        name: "yearsInOperation",
        type: "input",
        label: intl["kyc.information.operations.yearsinoperation.label"],
        validation: [{ required: true }],
      },
      {
        name: "numberOfStaff",
        type: "input",
        label: intl["kyc.information.operations.numberofstaff.label"],
        validation: [{ required: true }],
      },
      {
        name: "operatingCountries",
        type: "select",
        label: intl["kyc.information.operations.operatingcountry.label"],
        options: kycCountrySelectOptionList("commonName"),
        validation: [{ required: true }],
        multiple: true,
      },
      {
        name: "mostRecentNetProfit",
        type: "input",
        label: intl["kyc.information.operations.mostrecentnetprofit.label"],
        validation: [{ required: true }],
      },
      {
        name: "mostRecentNetAsset",
        type: "input",
        label: intl["kyc.information.operations.mostrecentnetasset.label"],
        validation: [{ required: true }],
      },
      {
        name: "financialYearForEstimates",
        type: "input",
        label: intl["kyc.information.operations.financialyearforestimates.label"],
        validation: [{ required: true }],
      },
      {
        name: "listedEntity",
        type: "listedEntity",
        label: intl["kyc.information.operations.listedentity.label"],
        options: [],
        validation: [{ required: true }],
      },
      {
        name: "uaeWhollyOwnedHolding",
        type: "radioWithInput",
        label: intl["kyc.information.operations.uaewhollyownedholding.label"],
        options: [
          ...createOptions([intl["kyc.v2.option.label.no"]]),
          { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
        ],
        validation: [{ required: true }],
      },
      {
        name: "politicallyExposedStakeholders",
        type: "radioWithInput",
        label: intl["kyc.information.operations.politicallyexposedstakeholders.label"],
        options: [
          ...createOptions([intl["kyc.v2.option.label.no"]]),
          { title: intl["kyc.v2.option.label.other"], value: CUSTOM_RADIO_WITH_INPUT },
        ],
        validation: [{ required: true }],
      },
    ],
  },
  // 股东信息 - 同名字段在同名group内
  {
    type: "group",
    name: "shareholders",
    label: intl["kyc.information.shareholders.label"],
    fields: [
      {
        name: "shareholders",
        type: "shareholders",
        label: intl["kyc.information.shareholders.shareholders.label"],
        validation: [{ required: true }],
      },
    ],
  },
  // 04. 最终受益所有人
  {
    type: "group",
    name: "owner",
    label: intl["kyc.v2.information.owner.label"],
    fields: [
      {
        name: "beneficiaries",
        type: "beneficiaries",
        label: intl["kyc.v2.information.owner.beneficiaries.label"],
        subLabel: intl["kyc.v2.information.owner.beneficiaries.sublabel"],
        validation: [{ required: true }],
      },
      {
        name: "wealthSourceOfOwner",
        type: "wealthSourceOfOwner",
        label: intl["kyc.v2.information.owner.wealthSourceOfOwner.label"],
        subLabel: intl["kyc.v2.information.owner.wealthSourceOfOwner.sublabel"],
        validation: [{ required: true }],
      },
    ],
  },
  // 05. 董事
  {
    type: "group",
    name: "directors",
    label: intl["kyc.v2.information.directors.label"],
    fields: [
      {
        name: "directors",
        type: "directors",
        label: intl["kyc.v2.information.directors.directors.label"],
        validation: [{ required: true }],
      },
    ],
  },
  // 06. 授权签字人
  {
    type: "group",
    name: "authorisedSignatories",
    label: intl["kyc.v2.information.authorisedSignatories.label"],
    fields: [
      {
        name: "authorisedSignatories",
        type: "authorisedSignatories",
        label: intl["kyc.v2.information.authorisedSignatories.authorisedSignatories.label"],
        validation: [{ required: true }],
      },
    ],
  },
  // 07. 最高级授权签字人
  {
    type: "group",
    name: "mostSeniorAuthorisedSignatory",
    label: intl["kyc.v2.information.mostSeniorAuthorisedSignatory.label"],
    subLabel: intl["kyc.v2.information.mostSeniorAuthorisedSignatory.sublabel"],
    fields: [
      {
        name: "name",
        type: "input",
        label: intl["kyc.v2.information.mostSeniorAuthorisedSignatory.name.label"],
        validation: [{ required: true }],
      },
      {
        name: "yearsOfInvestmentExperience",
        type: "categoryRadioGroup",
        label:
          intl[
            "kyc.v2.information.mostSeniorAuthorisedSignatory.yearsOfInvestmentExperience.label"
          ],
        categoryOptions: [
          {
            key: intl["kyc.v2.option.label.fund.products"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.virtual.assets"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.equities"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.derivatives"],
            options: YEARS_OF_INVESTMENT_EXPERIENCE_OPTIONS,
          },
        ],
        validation: [{ required: true }],
      },
      {
        name: "numberOfInvestmentsLastFiveYear",
        type: "categoryRadioGroup",
        label:
          intl[
            "kyc.v2.information.mostSeniorAuthorisedSignatory.numberOfInvestmentsLastFiveYear.label"
          ],
        categoryOptions: [
          {
            key: intl["kyc.v2.option.label.fund.products"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.virtual.assets"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.equities"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
          {
            key: intl["kyc.v2.option.label.derivatives"],
            options: NUMBER_OF_INVESTMENTS_LAST_FIVE_YEAR_OPTIONS,
          },
        ],
        validation: [{ required: true }],
      },
      {
        name: "knowledgeLevelOfDerivativesAndFunds",
        type: "checkbox",
        label:
          intl[
            "kyc.v2.information.mostSeniorAuthorisedSignatory.knowledgeLevelOfDerivativesAndFunds.label"
          ],
        options: createOptions([
          intl["kyc.v2.option.label.professional.certification"],
          intl["kyc.v2.option.label.work.experience.financial"],
          intl["kyc.v2.option.label.degree.finance"],
          intl["kyc.v2.option.label.trading.courses"],
          intl["kyc.v2.option.label.no.formal.training"],
        ]),
        validation: [{ required: true }],
      },
    ],
  },
  // 08. 资金来源
  {
    type: "group",
    name: "fundSource",
    label: intl["kyc.v2.information.fundSources.label"],
    fields: [
      {
        name: "fundSource",
        type: "institutionSourceOfFund",
        label: intl["kyc.v2.information.fundSources.fundSources.label"],
        validation: [{ required: true }],
      },
    ],
  },
];
